import React, { useCallback, useMemo } from 'react';

import { useTheme } from '@cobalt/react-theme-provider';
import Flex from '@cobalt/react-flex';
import Box from '@cobalt/react-box';
import Icon from '@cobalt/react-icon';
import Spinner from '@cobalt/react-spinner';

import { CascaderSelectorProps } from './CascaderSelector';

export interface CascaderTransIconProps
  extends Pick<
    CascaderSelectorProps,
    'isOpen' | 'disabled' | 'allowClear' | 'loading' | 'size' | 'className' | 'onToggleOpen' | 'onClearSelectedValue'
  > {
  hasSelected: boolean;
}

const CascaderTransIcon = (props: CascaderTransIconProps) => {
  const { isOpen, disabled, allowClear, loading, hasSelected, size, className, onToggleOpen, onClearSelectedValue } =
    props;

  const theme = useTheme();

  const showClearIcon = useMemo(() => {
    return !disabled && allowClear && hasSelected;
  }, [allowClear, disabled, hasSelected]);

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleOpen(false);
      onClearSelectedValue();
    },
    [onClearSelectedValue, onToggleOpen],
  );

  return (
    <Flex data-testid="cascaderTransIcon" className={className} alignX="end" alignY="center" paddingLeft="1" grow>
      {!loading && (
        <>
          {/* Clear icon */}
          {showClearIcon && (
            <Box paddingRight="2" onClick={handleClear}>
              <Icon
                style={{ display: 'block' }}
                name="clear"
                size={size === 'small' ? 'micro' : 'tiny'}
                color={theme.gray600}
              />
            </Box>
          )}

          {/* Expand icon */}
          <Icon name={isOpen ? 'expand_less' : 'expand_more'} size={size === 'small' ? 'tiny' : 'small'} />
        </>
      )}

      {/* Loading icon */}
      {loading && <Spinner data-testid="spinner" size={size === 'small' ? 'micro' : 'tiny'} />}
    </Flex>
  );
};

export default CascaderTransIcon;
